import React, { useEffect } from 'react';
import {
  ReceiptType,
  VENUE_IDS_WITH_CUSTOM_ADYEN,
  Vars,
  useDispatch,
  useI18n,
  useLogger,
  useReceiptGenerator,
  useSelector,
} from '~Hooks';
import { useIncrementRefundReceiptCounter } from '~Hooks/useReceiptCounter';
import { PAYMENT_TYPE } from './types';
import { useStorage } from '~Components/Storage';
import { Refund, RefundProps } from './types';
import { RefundIzettle } from './Methods/RefundIzettle';
import RefundAdyenBackend from './Methods/RefundAdyenBackend';
import { RefundAdyen } from './Methods/RefundAdyen';
import { RefundMicrodeb } from './Methods/RefundMicrodeb';
import RefundBaemingoGiftcard from './Methods/RefundBaemingoGiftcard';
import { RefundRetain24 } from './Methods/RefundRetain24';
import { RefundWaiterAidGiftcard } from './Methods/RefundWaiterAidGiftcard';
import { RefundOther } from './Methods/RefundOther';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { REFUND_APP_ORDER, REFUND_ITEM, REFUND_POS_ORDER, REFUND_TIP } from '../refund-gql';
import {
  refundAppOrder,
  refundAppOrderVariables,
  refundOrderItem,
  refundOrderItemVariables,
  refundPosOrder,
  refundPosOrderVariables,
  refundTip,
  refundTipVariables,
} from '../__queries__';
import { ORDER_ORIGIN, ROLE } from '../../../../__queries__';
import { useReportsApi } from '~Components/ReportsUniverse';
import {
  useCompetition,
  SUBTRACT_SALE_TYPE,
  SUBTRACT_TIP_TYPE,
} from '~Providers/CompetitionsProvider';

import { PrintRow } from '~Components/PrinterManager';
import { RefundCash } from './Methods/RefundCash';
import { CryptoRefund } from './Methods/RefundCrypto';
import { DateTime } from 'luxon';
import { getOrder, getOrderVariables } from './__queries__';
import { useDeviceHandler } from '~Components/DeviceProvider';

type Props = Refund & {
  generateReceipt: ReturnType<typeof useReceiptGenerator>;
  onFinished: (id: string) => void;
};

const GET_ORDER = gql`
  query getOrder($orderId: ID!) {
    Order(id: $orderId) {
      id
      createdAt
      easyId
      basketToken
      isTableOrder
      posDevice {
        name
      }
      user {
        id
        role
        venues {
          id
        }
      }
      payment {
        id
        amount
        tip
        correctedTip
        type
        createdAt
        transactionId
        status
        metadata {
          cardDetails {
            cardType
            cardPaymentEntryMode
            tsi
            tvr
            aid
            maskedPan
          }
        }
        subPayments {
          id
          createdAt
          updatedAt
          amount
          status
          type
          transactionId
          correctedTip
          tip
        }
      }
    }
  }
`;

/**
 * The Refund Controller handles the lifecycle for a single refund.
 *
 * When a refund is initiated, the controller will render the correct refund method
 * and perform the actual refund. Afterwards, it will call the backend to update the
 * refund status of the order/item/tip.
 *
 * Finally, it will call the onCompleted or onFail callback.
 */
export const RefundMethodsController = (props: Props) => {
  const dispatch = useDispatch();
  const [storage] = useStorage();
  const reportsApi = useReportsApi();
  const deviceHandler = useDeviceHandler();
  const country = useSelector((state) => state.country);
  const venueId = useSelector((state) => state.venueId);
  const receiptCounter = useSelector((state) => state.POSReceiptCounter);
  const companyFlags = useSelector((state) => state.companyFlags);
  const staffId = useSelector((state) => state.staff)?.id;
  const incrementRefundReceiptCounter = useIncrementRefundReceiptCounter();
  const { i18n } = useI18n();

  const competitions = useCompetition();

  const [refundOrder] = useMutation<refundPosOrder, refundPosOrderVariables>(REFUND_POS_ORDER, {
    refetchQueries: ['getSelectedOrder'],
  });

  const [refundItem] = useMutation<refundOrderItem, refundOrderItemVariables>(REFUND_ITEM, {
    refetchQueries: ['getSelectedOrder'],
  });

  const [tipRefund] = useMutation<refundTip, refundTipVariables>(REFUND_TIP, {
    refetchQueries: ['getSelectedOrder'],
  });

  const [refundApp] = useMutation<refundAppOrder, refundAppOrderVariables>(REFUND_APP_ORDER, {
    refetchQueries: ['getSelectedOrder'],
  });

  const [getOrderById] = useLazyQuery<getOrder, getOrderVariables>(GET_ORDER);

  const logger = useLogger('refunds');

  /**
   * If its a tip refund, use that value. Otherwise calculate amount
   * from items. We calculate here to save some logic for the consumer
   * of the refund API.
   */
  let amount =
    props.type === 'TIP'
      ? props.previousTipAmount - props.newTipAmount
      : props.items.reduce((sum, item) => sum + item.amount - item.discount, 0);

  if (props.type === 'ORDER') {
    amount += props.tipAmount;
  }

  /**
   * After the refund is completed, we need to update the backend.
   * Here, we need to take into account if its a order, item or tip refund and
   * call the corresponding mutation.
   */
  async function onCompleted() {
    logger.info('Refund - Completed');

    try {
      /**
       * Register the refund in the Z-Reports API
       */
      try {
        if (props.type !== 'TIP') {
          const originalOrder = await getOrderById({
            variables: {
              orderId: props.orderId,
            },
          });

          const subPayments = originalOrder.data?.Order.payment.subPayments || [];

          // Multipay order needs to be handled differently
          if (props.method === 'MULTIPAY' && subPayments.length > 1) {
            const paymentMethods = subPayments.map((payment) => ({
              amount: payment.amount,
              name: payment.type,
            }));

            reportsApi.refundOrder(
              props.orderId,
              props.items.map((item) => ({
                ...item,
                category: item.categoryId,
              })),
              paymentMethods,
              staffId,
            );
          } else {
            reportsApi.refundOrder(
              props.orderId,
              props.items.map((item) => ({
                ...item,
                category: item.categoryId,
              })),
              [
                {
                  // If its an order refund, we do this to prevent a double count of the tip.
                  // The tip correction below also deducts from the payment method.
                  amount: props.type === 'ORDER' ? amount - props.tipAmount : amount,
                  name: props.method,
                },
              ],
              staffId,
            );
          }
        }

        if (props.type === 'ORDER') {
          reportsApi.tipCorrection(-props.tipAmount, props.method, props.orderId);
        }

        if (props.type === 'TIP') {
          reportsApi.tipCorrection(-amount, props.method, props.orderId);
        }
      } catch (e) {
        logger.error('Refund - Z-Reports API failed!', { err: (e as any).message, refund: props });
      }

      // Compute refund metadata upfront so it's available for both backend calls and receipt generation
      let refundReceiptNumber: number | undefined;
      if (props.type !== 'TIP') {
        if (country === 'ES' && companyFlags.useNewLicenses) {
          try {
            refundReceiptNumber = await incrementRefundReceiptCounter();
          } catch (e) {
            logger.error('Refund receipt counter increment failed, proceeding without counter', {
              err: e instanceof Error ? e.message : String(e),
              orderId: props.orderId,
            });
          }
        } else {
          refundReceiptNumber = receiptCounter;
        }
      }
      const refundDeviceId = storage.deviceId || undefined;

      /**
       * Update the order / tip in the backend
       */
      if (props.type === 'ORDER') {
        if (props.origin === ORDER_ORIGIN.APP) {
          try {
            const res = await refundApp({
              variables: {
                orderId: props.orderId,
                refundReceiptNumber: refundReceiptNumber ? refundReceiptNumber : undefined,
                deviceId: refundDeviceId,
              },
            });

            if (res.data) {
              logger.info('Order refund - Backend update success!', { props });
            }

            if (res.errors) {
              logger.error('App refund - Backend update failed!', {
                err: res.errors,
                props,
              });
            }
          } catch (e) {
            logger.error('App refund - Backend update failed!', {
              err: e,
              props,
            });
          }
        } else {
          try {
            const res = await refundOrder({
              variables: {
                orderId: props.orderId,
                refundReceiptNumber: refundReceiptNumber ? refundReceiptNumber : undefined,
                deviceId: refundDeviceId,
              },
            });

            if (res.data) {
              logger.info('Order refund - Backend update success!', { props });
            }

            if (res.errors) {
              logger.error('Order refund - Backend update failed!', {
                err: res.errors,
                props,
              });
            }
          } catch (e) {
            logger.error('Order refund - Backend update failed!', {
              err: e,
              props,
            });
          }
        }

        /**
         * STAFF COMPETITIONS: A full refund has been made, so we need to try and subtract the sale from the waiter in the competitions
         */
        if (props.waiterId) {
          competitions.subtractSale({
            staffId: props.waiterId,
            items: props.items,
            type: SUBTRACT_SALE_TYPE.REFUND_ORDER,
          });

          competitions.subtractTip({
            staffId: props.waiterId,
            tip: props.tipAmount,
            type: SUBTRACT_TIP_TYPE.REFUND_FULL_TIP,
          });
        }
      } else if (props.type === 'ITEM') {
        for (const item of props.items) {
          try {
            const res = await refundItem({
              variables: {
                itemId: item.id,
                orderId: props.orderId,
                refundReceiptNumber: refundReceiptNumber ? refundReceiptNumber : undefined,
                deviceId: refundDeviceId,
              },
            });

            if (res.data) {
              logger.info('Item refund - Backend update success!', { props });
            }

            if (res.errors) {
              logger.error('Item refund - Backend update failed!', {
                err: res.errors,
                props,
                item,
              });
            }
          } catch (e) {
            logger.error('Item refund - Backend update failed!', {
              err: e,
              props,
              item,
            });
          }
        }

        /**
         * STAFF COMPETITIONS: Subtract the sale from the waiter in the competitions
         */
        if (props.waiterId) {
          competitions.subtractSale({
            staffId: props.waiterId,
            items: props.items,
            type: SUBTRACT_SALE_TYPE.REMOVE_ITEM,
          });
        }
      } else {
        try {
          const res = await tipRefund({
            variables: {
              orderId: props.orderId,
              tipAmount: props.newTipAmount,
              transactions: [
                {
                  amount: amount,
                  type: props.method,
                  transactionId: props.transactionId,
                },
              ],
            },
          });

          if (res.data) {
            logger.info('Tip refund - Backend update success!', { props });
          }

          if (res.errors) {
            logger.error('Tip refund - Backend update failed!', {
              err: res.errors,
              props,
            });
          }

          /**
           * STAFF COMPETITIONS: Subtract the tip from the waiter in the competitions
           */
          if (props.waiterId) {
            competitions.subtractTip({
              staffId: props.waiterId,
              tip: props.previousTipAmount - props.newTipAmount,
              type: SUBTRACT_TIP_TYPE.TIP_CORRECTION,
            });
          }
        } catch (e) {
          logger.error('Tip refund - Backend update failed!', {
            err: e,
            props,
          });
        }
      }

      /**
       * Print the refund receipt.
       *
       * We only do this for order and item refunds
       */
      try {
        const originalOrder = await getOrderById({
          variables: {
            orderId: props.orderId,
          },
        });
        const originalOrderData = originalOrder.data?.Order;
        const orderDeviceName = originalOrderData?.posDevice?.name || 'Unknown Device';

        let userId: string | undefined = undefined;
        const isBackofficeAdmin =
          originalOrderData?.user.role === ROLE.CONTENT_ADMIN ||
          (originalOrderData?.user.venues ?? []).length > 0;

        if (!isBackofficeAdmin && country === 'ES') {
          userId = originalOrderData?.user?.id;
        }

        if (props.type !== 'TIP') {
          const originalReceiptCounter = originalOrderData
            ? originalOrderData?.basketToken?.split('-')[1]
            : undefined;

          const orderDate = originalOrderData?.createdAt
            ? DateTime.fromISO(originalOrderData?.createdAt)
            : '';

          const extraVars: Vars = {
            checkId: receiptCounter.toString(),
            deviceName: storage.taxComplianceDeviceName || '',
            deviceId: storage.taxComplianceControlUnitSerial || '',
            orderDate: orderDate && orderDate.toFormat('yyyy-MM-dd HH:mm'),
            easyId:
              (originalOrderData?.isTableOrder ? i18n('Receipt.Table') + ': ' : '#') +
              originalOrderData?.easyId,
          };

          // Spain uses refundNumber/refundReference instead of checkId/orderDate
          if (country === 'ES' && companyFlags.useNewLicenses) {
            extraVars.checkId = undefined;
            extraVars.orderDate = undefined;
            extraVars.refundNumber =
              'FR-' + deviceHandler.activeDevice?.name + '-' + refundReceiptNumber?.toString();
            extraVars.refundReference =
              (originalOrderData?.user.id && originalOrderData?.user.role === 'REGULAR'
                ? 'FO-'
                : '') +
              orderDeviceName +
              '-' +
              originalReceiptCounter +
              '  (' +
              (orderDate && orderDate.toFormat('MM/dd/yyyy')) +
              ')';
          }

          if (props.type === 'ORDER') {
            extraVars.tip = props.tipAmount.toString();
            extraVars.paymentIncTip = amount.toString();
          }

          try {
            const receipt = await props.generateReceipt({
              type: ReceiptType.Refund,
              items: props.items.map((item) => ({
                amount: item.amount,
                discount: item.discount,
                entryId: item.entryId,
                percentageVAT: item.vatRate,
                title: item.title,
                fraction: item.fraction,
              })),
              totalDiscount: 0,
              extraVars,
              payments: originalOrderData
                ? originalOrderData.payment.subPayments.map((payment) => ({
                    type: payment.type,
                    amount: (payment.amount / 100).toFixed(2),
                  }))
                : undefined,
              cardDetails: originalOrderData?.payment?.metadata?.cardDetails,
              userId,
            });

            if (receipt) {
              const lines: PrintRow[] = receipt.lines.map((line) => ({
                fontSize: line.fontSize || (line.doubleHeigth || line.doubleWidth ? 26 : 13), // doubleHeight here can come from the receipt package
                content: line.text,
              }));

              window.printerManager?.initiatePrint({
                data: lines,
                orderId: props.orderId,
                printType: 'RECEIPT',
                base64Image: receipt.base64Image,
              });

              const refundAmount = props.items.reduce(
                (sum, item) => sum + item.amount - item.discount,
                0,
              );
              if (props.type === 'ITEM') {
                reportsApi.refundReceiptPrinted(refundAmount);
              } else if (props.type === 'ORDER') {
                reportsApi.refundReceiptPrinted(refundAmount);
              }

              // For non-Spain: increment the shared local receipt counter
              if (country !== 'ES') {
                dispatch({ type: 'INCREASE_RECEIPT_COUNTER' });
              }
              reportsApi.receiptCopyPrinted(refundAmount);

              // Receipt printed successfully
              props.onCompleted();

              return;
            } else {
              throw new Error('Receipt generation failed - receipt is null');
            }
          } catch (error) {
            // Log complete error details for ORDER/ITEM refund receipt generation
            logger.error('Refund (ORDER/ITEM) - Receipt generation failed!', {
              err: error,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined,
              props,
              extraVars,
            });

            // Call onCompleted with receipt error message to inform user
            const errorMessage =
              error instanceof Error ? error.message : 'Receipt generation failed';
            props.onCompleted(errorMessage);

            return;
          }
        }

        if (props.type === 'TIP') {
          const extraVars: Vars = {
            deviceName: storage.taxComplianceDeviceName || '',
            deviceId: storage.taxComplianceControlUnitSerial || '',
            orderDate: originalOrderData?.createdAt
              ? DateTime.fromISO(originalOrderData?.createdAt).toFormat('yyyy-MM-dd HH:mm')
              : '',
            easyId:
              (originalOrderData?.isTableOrder ? i18n('Receipt.Table') + ': ' : '#') +
              originalOrderData?.easyId,
            tipRefunded: ((props.previousTipAmount - props.newTipAmount) / 100).toFixed(2),
          };

          try {
            const receipt = await props.generateReceipt({
              type: ReceiptType.TipRefund,
              items: [],
              extraVars,
              totalDiscount: 0,
              payments: originalOrderData
                ? originalOrderData.payment.subPayments.map((payment) => ({
                    type: payment.type,
                    amount: (payment.amount / 100).toFixed(2),
                  }))
                : undefined,
              cardDetails: originalOrderData?.payment?.metadata?.cardDetails,
              userId,
            });

            if (receipt) {
              const lines: PrintRow[] = receipt.lines.map((line) => ({
                fontSize: line.fontSize || (line.doubleHeigth || line.doubleWidth ? 26 : 13), // doubleHeight here can come from the receipt package
                content: line.text,
              }));

              window.printerManager?.initiatePrint({
                data: lines,
                orderId: props.orderId,
                printType: 'RECEIPT',
                base64Image: receipt.base64Image,
              });

              reportsApi.receiptCopyPrinted(0);
            }
          } catch (error) {
            logger.error('Tip Refund - Receipt generation failed!', { err: error, props });
          }
        }

        // Receipt printed successfully
        props.onCompleted();
      } catch (e) {
        // Receipt printing failed, but refund succeeded
        logger.error('Refund - Receipt generation failed!', { err: e, props });

        // Call onCompleted with receipt error message to inform user
        const errorMessage = e instanceof Error ? e.message : 'Receipt generation failed';
        props.onCompleted(errorMessage);
      }
    } catch (e) {
      logger.error('Refund - Backend update failed! This should not happen!!', {
        err: e as any,
        props,
      });
      props.onFail();
    }

    props.onFinished(props.transactionId);
  }

  function onFail(message?: string) {
    logger.info('Refund - Failed', props);
    props.onFail(message);
  }

  /**
   * Log the method
   */
  useEffect(() => {
    logger.info(`Refund - ${props.method} refund triggered`, {
      props,
    });
  }, []);

  const methodProps: RefundProps = {
    amount,
    onCompleted,
    onFail,
    waiterId: props.waiterId,
    method: props.method,
    orderId: props.orderId,
    origin: props.origin,
    transactionCreatedAt: props.transactionCreatedAt,
    transactionId: props.transactionId,
  };

  switch (props.method) {
    case PAYMENT_TYPE.IZETTLE:
      return <RefundIzettle {...methodProps} />;
    case PAYMENT_TYPE.CRYPTO:
      return <CryptoRefund {...methodProps} />;
    case PAYMENT_TYPE.ADYEN:
      // If it's an app order, it's refunded in the backend call above
      // so we just use the "Other" method wich does nothing
      return props.origin === ORDER_ORIGIN.APP ? (
        <RefundOther {...methodProps} />
      ) : !VENUE_IDS_WITH_CUSTOM_ADYEN.includes(venueId!) ? (
        <RefundAdyenBackend {...methodProps} />
      ) : (
        <RefundAdyen {...methodProps} />
      );
    case PAYMENT_TYPE.MICRODEB:
      return <RefundMicrodeb {...methodProps} />;
    case PAYMENT_TYPE.BAEMINGO_GIFTCARD:
      return <RefundBaemingoGiftcard {...methodProps} />;
    case PAYMENT_TYPE.RETAIN24:
      return <RefundRetain24 {...methodProps} />;
    case PAYMENT_TYPE.WAITERAID:
      return <RefundWaiterAidGiftcard {...methodProps} />;
    case PAYMENT_TYPE.CASH:
      return <RefundCash {...methodProps} />;
    default:
      return <RefundOther {...methodProps} />;
  }
};
